/**
 * BidNotesScope
 * -------------
 * Smart scope-of-work statement for a notes-based opportunity. Divisions are
 * generated off the engineering brief, the calculation sheet and the BOM —
 * Electrical, Plumbing / distribution loop, Construction & containment, the RO
 * system and its components, DI polishing tanks, controls and commissioning —
 * then edited in place before the proposal goes out.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { openAuthedDoc } from "@/lib/openAuthedDoc";
import {
  Loader2, Hammer, RefreshCw, Download, FileText, Save, Plus, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";

const BE = process.env.REACT_APP_BACKEND_URL;
const RESP = ["Sterile Mate", "General Contractor", "Owner", "By Others"];
const LISTS = [
  ["codes_standards", "Codes & standards"],
  ["assumptions", "Assumptions"],
  ["clarifications", "Clarifications"],
  ["exclusions", "Exclusions"],
  ["deliverables", "Deliverables"],
];

const toText = (arr) => (arr || []).join("\n");
const toArr = (txt) => (txt || "").split("\n").map((s) => s.trim()).filter(Boolean);

export default function BidNotesScope({ noteId, token, initialScope, initialStatus }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [scope, setScope] = useState(initialScope || null);
  const [status, setStatus] = useState(initialStatus || (initialScope ? "ready" : null));
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (status !== "generating") return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/bid-desk/notes/${noteId}/scope?fmt=json`, { headers });
        if (r.data.scope_status !== "generating") {
          setStatus(r.data.scope_status);
          setScope(r.data.scope_of_work || null);
          setError(r.data.scope_error || null);
          if (r.data.scope_status === "ready") toast.success("Scope of work drafted");
        }
      } catch { /* keep polling */ }
    }, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, noteId]);

  const generate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/bid-desk/notes/${noteId}/scope`, {}, { headers });
      setStatus("generating");
      toast.info("Writing the scope of work from the brief, calcs and BOM…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the scope");
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/bid-desk/notes/${noteId}/scope`, { scope_of_work: scope }, { headers });
      toast.success("Scope saved");
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const editDiv = (i, key, value) => setScope((s) => ({
    ...s, divisions: s.divisions.map((d, idx) => (idx === i ? { ...d, [key]: value } : d)),
  }));

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="notes-scope">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="text-sm font-black uppercase tracking-wider text-slate-700 inline-flex items-center gap-1.5">
            <Hammer className="w-4 h-4 text-slate-400" /> Smart scope of work
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Electrical · Plumbing &amp; distribution loop · Construction, containment &amp; barriers ·
            RO system &amp; components · DI polishing tanks · Controls · Commissioning
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={generate} disabled={busy || status === "generating"}
            data-testid="scope-generate-btn">
            {status === "generating" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            {scope ? "Regenerate" : "Generate scope"}
          </Button>
          {scope && (
            <>
              <Button size="sm" variant="outline" onClick={save} disabled={busy} data-testid="scope-save-btn">
                <Save className="w-3.5 h-3.5 mr-1" /> Save
              </Button>
              <button onClick={() => openAuthedDoc(`/bid-desk/notes/${noteId}/scope`, { token, fmt: "html" })}
                className="inline-flex items-center h-8 px-2.5 rounded-md border border-slate-300 text-xs hover:bg-slate-50">
                <FileText className="w-3.5 h-3.5 mr-1" /> Preview
              </button>
              <button onClick={() => openAuthedDoc(`/bid-desk/notes/${noteId}/scope`, { token, fmt: "pdf" })}
                className="inline-flex items-center h-8 px-2.5 rounded-md border border-slate-300 text-xs hover:bg-slate-50"
                data-testid="scope-pdf-link">
                <Download className="w-3.5 h-3.5 mr-1" /> PDF
              </button>
            </>
          )}
        </div>
      </div>

      {status === "generating" && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2.5 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Writing the division-by-division scope — about a minute.
        </p>
      )}
      {status === "failed" && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5">
          <AlertTriangle className="w-4 h-4 inline mr-1" /> {error || "Scope generation failed."}
        </p>
      )}
      {!scope && status !== "generating" && status !== "failed" && (
        <p className="text-sm text-slate-500">
          Once the brief and the calculation sheet are set, generate the scope statement —
          it only uses numbers from the calc sheet, so nothing is invented.
        </p>
      )}

      {scope && (
        <div className="space-y-3">
          {scope.summary && (
            <Textarea value={scope.summary} rows={3} className="text-xs"
              onChange={(e) => setScope((s) => ({ ...s, summary: e.target.value }))}
              data-testid="scope-summary" />
          )}

          {(scope.divisions || []).map((d, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-3" data-testid={`scope-div-${i}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Input value={d.code || ""} className="h-8 text-xs w-24 sm:w-40"
                  onChange={(e) => editDiv(i, "code", e.target.value)} />
                <Input value={d.name || ""} className="h-8 text-xs flex-1 min-w-[120px] sm:min-w-[180px] font-semibold"
                  onChange={(e) => editDiv(i, "name", e.target.value)} />
                <select value={d.responsibility || RESP[0]}
                  onChange={(e) => editDiv(i, "responsibility", e.target.value)}
                  className="h-8 text-xs border border-slate-300 rounded-md px-1.5"
                  data-testid={`scope-resp-${i}`}>
                  {RESP.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">
                Scope items — one per line
              </p>
              <Textarea value={toText(d.scope_items)} rows={Math.min(12, (d.scope_items || []).length + 2)}
                className="text-xs" onChange={(e) => editDiv(i, "scope_items", toArr(e.target.value))}
                data-testid={`scope-items-${i}`} />
              {!!(d.materials || []).length && (
                <>
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mt-2 mb-1">
                    Materials &amp; equipment
                  </p>
                  <Textarea value={toText(d.materials)} rows={Math.min(8, (d.materials || []).length + 1)}
                    className="text-xs" onChange={(e) => editDiv(i, "materials", toArr(e.target.value))} />
                </>
              )}
              {!!(d.coordination || []).length && (
                <>
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mt-2 mb-1">
                    Coordination
                  </p>
                  <Textarea value={toText(d.coordination)} rows={Math.min(8, (d.coordination || []).length + 1)}
                    className="text-xs" onChange={(e) => editDiv(i, "coordination", toArr(e.target.value))} />
                </>
              )}
            </div>
          ))}

          <Button size="sm" variant="outline" data-testid="scope-add-div"
            onClick={() => setScope((s) => ({ ...s, divisions: [...(s.divisions || []),
              { code: "", name: "New division", responsibility: RESP[0], scope_items: [] }] }))}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Division
          </Button>

          {!!(scope.responsibility_matrix || []).length && (
            <div className="overflow-x-auto">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">
                Responsibility matrix
              </p>
              <table className="w-full min-w-[640px] text-xs">
                <thead className="bg-slate-50">
                  <tr>{["Item", "Sterile Mate", "General Contractor", "Owner"].map((h) => (
                    <th key={h} className="text-left px-2 py-1.5 font-black uppercase tracking-wider text-[9px] text-slate-500">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {scope.responsibility_matrix.map((m, i) => (
                    <tr key={i}>
                      <td className="px-2 py-1.5 text-slate-700">{m.item}</td>
                      <td className="px-2 py-1.5 text-slate-600">{m.sterile_mate}</td>
                      <td className="px-2 py-1.5 text-slate-600">{m.gc}</td>
                      <td className="px-2 py-1.5 text-slate-600">{m.owner}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-3">
            {LISTS.map(([key, label]) => (
              <div key={key}>
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">{label}</p>
                <Textarea value={toText(scope[key])} rows={4} className="text-xs"
                  onChange={(e) => setScope((s) => ({ ...s, [key]: toArr(e.target.value) }))}
                  data-testid={`scope-${key}`} />
              </div>
            ))}
          </div>

          {!!(scope.milestones || []).length && (
            <div>
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Schedule milestones</p>
              <ul className="space-y-0.5">
                {scope.milestones.map((m, i) => (
                  <li key={i} className="text-xs text-slate-700">• {m.name} — <strong>{m.duration}</strong></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
